import React from 'react';
import { Link } from 'react-router-dom';
import { LegalPageLayout } from '../legal/LegalPageLayout';

export function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      meta="Last updated: 11 August 2025 · Tarana Media Private Limited"
    >
      <section className="terms-section" id="executive-summary">
                    <h2 className="terms-h2">
                      1. Executive Summary
                    </h2>
                    <p>
                      Tarana is an innovative music platform connecting Creators
                      (Artists, Organizations) and Non-Creators (Listeners) for music
                      streaming, social networking, collaboration, freelancing, and
                      content monetization. This Privacy Policy outlines Tarana Media
                      Private Limited&apos;s (&quot;Tarana,&quot; &quot;we,&quot;
                      &quot;us,&quot; or &quot;our&quot;) practices for collecting,
                      using, processing, storing, and sharing personal data across its
                      services (website, mobile apps, APIs).
                    </p>
                    <p >
                      The policy ensures compliance with India&apos;s Digital Personal
                      Data Protection Act, 2023 (DPDPA), and other relevant legal
                      frameworks, emphasizing Tarana&apos;s commitment to user privacy
                      and data security. Our data handling adheres to DPDPA
                      principles: explicit consent, purpose limitation, data
                      minimization, accuracy, storage limitation, reasonable security
                      safeguards, and accountability.
                    </p>
                  </section>
      
                  <section className="terms-section" id="introduction">
                    <h2 className="terms-h2">
                      2. Introduction to Tarana and the Scope of the Privacy Policy
                    </h2>
      
                    <p>
                      Tarana is a multifaceted digital hub enabling Creators to
                      produce, upload, distribute, collaborate on, and monetize music
                      and video content, while offering team management and
                      freelancing opportunities. Listeners can stream content, engage
                      with creators, and hire creators.
                    </p>
      
                    <p >
                      User categories, all considered &quot;Data Principals&quot;
                      under DPDPA, include:
                    </p>
      
                    <ul className="terms-list">
                      <li>
                        <strong>Creators (Artists, Organizations):</strong> Create,
                        upload, distribute, Manage teams, collaborate, and monetize
                        content or offer services.
                      </li>
                      <li>
                        <strong>Non-Creators (Listeners):</strong> Consume content,
                        build playlists, hire creators.
                      </li>
                    </ul>
      
                    <p >
                      &quot;User&quot; encompasses any individual or entity using the
                      Services.
                    </p>
                    <p >
                      This Privacy Policy applies to all personal data collected
                      across Tarana&apos;s digital ecosystem, including its website,
                      mobile applications, and APIs. It is an integral part of
                      Tarana&apos;s Terms of Use. Data flows dynamically among user
                      types; for instance, an Artist&apos;s public profile is
                      accessible to Listeners. Tarana&apos;s approach is holistic,
                      transparently communicating how data from various activities and
                      features might be visible to others or combined to enhance
                      personalization, collaboration, and service delivery.
                    </p>
                  </section>
      
                  <section className="terms-section" id="legal-framework">
                    <h2 className="terms-h2">
                      3. Legal Framework: Digital Personal Data Protection Act, 2023
                      (DPDPA)
                    </h2>
      
                    <p>
                      Tarana Media Private Limited adheres to the principles and
                      obligations outlined in the Digital Personal Data Protection
                      Act, 2023 (DPDPA) for the responsible handling of personal data.
                    </p>
      
                    <p >
                      Our data practices are designed to reflect the core principles
                      of the DPDPA, including:
                    </p>
      
                    <ul className="terms-list">
                      <li>Lawful, fair, and transparent processing</li>
                      <li>
                        Purpose limitation — data is collected for specific,
                        legitimate uses only
                      </li>
                      <li>
                        Data minimization — only data necessary for our services is
                        collected
                      </li>
                      <li>
                        Data accuracy — users can correct or update their information
                      </li>
                      <li>
                        Storage limitation — data is retained only as long as required
                      </li>
                      <li>
                        Security safeguards — we employ strong technical and
                        organizational protections
                      </li>
                      <li>
                        Accountability — we take full responsibility for how personal
                        data is handled across our platform
                      </li>
                    </ul>
      
                    <p >
                      Tarana is committed to ongoing alignment with applicable data
                      protection laws and will continue enhancing its privacy and
                      security practices in accordance with evolving legal standards
                      in India.
                    </p>
                  </section>
      
                  <section className="terms-section" id="data-collection">
                    <h2 className="terms-h2">
                      4. Data Collection: What, Why, and How
                    </h2>
      
                    <p>
                      Tarana collects various personal data categories, adhering to
                      DPDPA principles of purpose limitation and data minimization.
                    </p>
      
                    <ul className="terms-list">
                      <li>
                        <strong>User Data (Provided by You):</strong> Information
                        directly supplied during account creation or profile setup.
                        <ul className="terms-list">
                          <li>
                            <strong>Examples:</strong> Name, email, date of birth,
                            phone number, profile photo, username, gender, country,
                            language, payment method type (truncated card digits),
                            ZIP/postal code.
                          </li>
                          <li>
                            <strong>Purpose:</strong> Account creation/management,
                            identity verification, core service delivery,
                            communication, payment processing.
                          </li>
                          <li>
                            <strong>Lawful Basis:</strong> User consent (for optional
                            info) and contract performance (for service delivery).
                          </li>
                        </ul>
                      </li>
      
                      <li>
                        <strong>Usage Data (Automatically Collected):</strong>{" "}
                        Information generated through interactions with Services.
                        <ul className="terms-list">
                          <li>
                            <strong>Examples:</strong> IP address, device identifiers,
                            network type, browser, OS, app version, URL info,
                            streaming history, search queries, content interactions
                            (likes, comments, shares), playlists, follower data, ad
                            interactions.
                          </li>
                          <li>
                            <strong>Purpose:</strong> Service delivery (streaming,
                            recommendations), user experience enhancement, platform
                            improvement, analytics, ad delivery, security, fraud
                            prevention.
                          </li>
                          <li>
                            <strong>Lawful Basis:</strong> Legitimate use (core
                            functionality, security, analytics) and consent (for
                            personalization, targeted advertising where applicable).
                          </li>
                        </ul>
                      </li>
      
                      <li>
                        <strong>Financial Data:</strong> Information related to
                        payments and transactions.
                        <ul className="terms-list">
                          <li>
                            <strong>Examples:</strong> Payment method type, last few
                            card digits, billing address, transaction history,
                            earnings data for Artists/Freelancers.
                          </li>
                          <li>
                            <strong>Purpose:</strong> Payment processing, freelancing
                            management, monetization payouts, fraud prevention, tax
                            compliance.
                          </li>
                          <li>
                            <strong>Lawful Basis:</strong> Contract performance and
                            legal obligations (tax/financial compliance).
                          </li>
                        </ul>
                      </li>
      
                      <li>
                        <strong>Know Your Customer (KYC) Data:</strong> Mandatory for
                        creators, freelancers, and users receiving payouts.
                        <ul className="terms-list">
                          <li>
                            <strong>Examples:</strong> Full legal name, date of birth,
                            residential address, government ID (PAN Card), live photo
                            for verification, tax documentation. For Organizations:
                            business registration, KYC for signatories.
                          </li>
                          <li>
                            <strong>Purpose:</strong> Secure transactions, trusted
                            connections, illicit activity prevention, legal compliance
                            (financial services).
                          </li>
                          <li>
                            <strong>Lawful Basis:</strong> Primarily legal obligation;
                            secondarily, consent for initial collection.
                          </li>
      
                          <p >
                            KYC data builds trust and integrity. It's mandatory for
                            financial transactions to "ensure secure transactions and
                            trusted connections" and "prevent illicit activities".
                            Robust identity verification mitigates fraud, enhances
                            freelancer credibility, and creates a safer environment.
                            The Privacy Policy frames KYC as a critical pillar for
                            security, integrity, and trustworthiness of Tarana's
                            financial features, increasing user acceptance.
                          </p>
                        </ul>
                      </li>
      
                      <li>
                        <strong>User-Generated Content (UGC) and Metadata:</strong>{" "}
                        All uploaded content and associated descriptive information.
                        <ul className="terms-list">
                          <li>
                            <strong>Examples:</strong> Audio/video files (singles,
                            albums, drafts), text (comments, posts, chat), graphics,
                            images, content descriptions.
                          </li>
                          <li>
                            <strong>Purpose:</strong> Content distribution, portfolio
                            creation, social interaction, collaboration, monetization,
                            platform moderation (
                            <Link to="/community-guidelines">Community Guidelines</Link>{" "}
                            compliance).
                          </li>
                          <li>
                            <strong>Lawful Basis:</strong> User consent (by uploading)
                            and contract performance (collaboration, monetization).
                          </li>
                        </ul>
                      </li>
      
                      <li>
                        <strong>Survey and Research Data:</strong> Voluntarily
                        provided in surveys or research.
                        <ul className="terms-list">
                          <li>
                            <strong>Purpose:</strong> Product improvement, user
                            experience enhancement.
                          </li>
                          <li>
                            <strong>Lawful Basis:</strong> User explicit consent.
                          </li>
                        </ul>
                      </li>
                    </ul>
                    <section className="terms-section" id="data-collection-table">
                      <h4
                        
                        className="terms-h4"
                      >
                        Table 1: Data Collection & Purpose Mapping
                      </h4>
      
                      <div className="terms-table-wrap">
                        <table className="terms-table">
                          <thead >
                            <tr>
                              <th className="terms-table-cell">
                                Feature / User Type
                              </th>
                              <th className="terms-table-cell">
                                Data Categories Collected
                              </th>
                              <th className="terms-table-cell">
                                Specific Data Points (Examples)
                              </th>
                              <th className="terms-table-cell">
                                Lawful Basis for Processing
                              </th>
                              <th className="terms-table-cell">
                                Purpose of Collection
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="terms-table-cell">
                                Account Creation &amp; Profile
                              </td>
                              <td className="terms-table-cell">
                                User Data
                              </td>
                              <td className="terms-table-cell">
                                Name, Email, DOB, Phone, Profile Photo, Username
                              </td>
                              <td className="terms-table-cell">
                                Consent, Performance of Contract
                              </td>
                              <td className="terms-table-cell">
                                Account setup, identity, communication,
                                personalization
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Music Streaming
                              </td>
                              <td className="terms-table-cell">
                                Usage Data
                              </td>
                              <td className="terms-table-cell">
                                Streaming history, search queries, content
                                interactions
                              </td>
                              <td className="terms-table-cell">
                                Legitimate Use, Consent
                              </td>
                              <td className="terms-table-cell">
                                Personalization, recommendations, platform
                                improvement, ad delivery
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Social Networking &amp; Community
                              </td>
                              <td className="terms-table-cell">
                                User Data, UGC, Usage Data
                              </td>
                              <td className="terms-table-cell">
                                Profile info, comments, likes, shares, direct
                                messages, follower data
                              </td>
                              <td className="terms-table-cell">
                                Consent, Legitimate Use
                              </td>
                              <td className="terms-table-cell">
                                Social interaction, community building, content
                                discovery
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Collaboration Tools
                              </td>
                              <td className="terms-table-cell">
                                User Data, UGC
                              </td>
                              <td className="terms-table-cell">
                                Shared workspaces, uploaded files (stems, mixes), task
                                management data, in-app messages
                              </td>
                              <td className="terms-table-cell">
                                Performance of Contract, Consent
                              </td>
                              <td className="terms-table-cell">
                                Project coordination, content co-creation, team
                                management
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Freelancing &amp; Hiring
                              </td>
                              <td className="terms-table-cell">
                                User Data, Financial Data, KYC Data, UGC (portfolio)
                              </td>
                              <td className="terms-table-cell">
                                Profile, pricing, reviews, job postings, bids, payment
                                info, escrow data, PAN, live photo
                              </td>
                              <td className="terms-table-cell">
                                Performance of Contract, Legal Obligation
                              </td>
                              <td className="terms-table-cell">
                                Marketplace facilitation, secure payments, identity
                                verification, fraud prevention
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Direct Upload &amp; Monetization
                              </td>
                              <td className="terms-table-cell">
                                User Data, Financial Data, KYC Data, UGC
                              </td>
                              <td className="terms-table-cell">
                                Uploaded music/video, earnings dashboard, payout info,
                                PAN, tax docs
                              </td>
                              <td className="terms-table-cell">
                                Performance of Contract, Legal Obligation
                              </td>
                              <td className="terms-table-cell">
                                Content distribution, revenue sharing, compliance
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Advertising &amp; Revenue Sharing
                              </td>
                              <td className="terms-table-cell">
                                Usage Data
                              </td>
                              <td className="terms-table-cell">
                                Ad interactions, content consumption patterns
                              </td>
                              <td className="terms-table-cell">
                                Legitimate Use, Consent
                              </td>
                              <td className="terms-table-cell">
                                Ad placement, revenue calculation, targeted
                                advertising (if consented)
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                KYC &amp; Verification
                              </td>
                              <td className="terms-table-cell">
                                KYC Data
                              </td>
                              <td className="terms-table-cell">
                                Government ID (PAN), live photo, residential address,
                                tax documentation
                              </td>
                              <td className="terms-table-cell">
                                Legal Obligation
                              </td>
                              <td className="terms-table-cell">
                                Identity verification, financial compliance, fraud
                                prevention
                              </td>
                            </tr>
                            <tr>
                              <td className="terms-table-cell">
                                Platform Administration &amp; Security
                              </td>
                              <td className="terms-table-cell">
                                Usage Data, User Data
                              </td>
                              <td className="terms-table-cell">
                                System logs, security alerts, login attempts, device
                                info
                              </td>
                              <td className="terms-table-cell">
                                Legitimate Use, Legal Obligation
                              </td>
                              <td className="terms-table-cell">
                                Platform maintenance, security, fraud detection, abuse
                                prevention
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </section>
      
                  <section className="terms-section" id="data-usage-processing">
                    <h2 className="terms-h2">
                      5. Data Usage and Processing
                    </h2>
      
                    <p>
                      Collected data is processed by Tarana to enable core platform
                      functionalities, adhering to purpose limitation and data
                      minimization.
                    </p>
      
                    <h3 className="terms-h3">
                      Account Management &amp; Service Delivery
                    </h3>
                    <p>
                      User Data manages accounts and grants access to features
                      (social, collaboration, freelancing, streaming). It facilitates
                      content delivery. Content and Usage Data enable personalized
                      recommendations, seamless streaming, and curated playlists.
                    </p>
      
                    <h3 className="terms-h3">
                      Personalization &amp; Recommendations
                    </h3>
                    <p>
                      Tarana uses Usage Data — such as listening history, genre
                      preferences, content interactions, and browsing behavior — to
                      deliver personalized recommendations for music, posts, and job
                      opportunities. This includes suggesting relevant content and
                      creator updates, similar to how platforms like Instagram
                      recommend posts and reels based on user interests.
                    </p>
                    <p >
                      While personalization relies on analyzing user activity, Tarana
                      adheres to the principles of purpose limitation and data
                      minimization under the Digital Personal Data Protection Act,
                      2023 (DPDPA).
                    </p>
                    <p >
                      To ensure transparency and user autonomy, Tarana provides
                      granular in-app privacy controls that allow users to manage,
                      limit, or opt out of personalization features at any time. These
                      controls enable users to make informed choices about their data,
                      reinforcing trust and our commitment to responsible data
                      practices.
                    </p>
      
                    <h3 className="terms-h3">
                      Monetization, Freelancing, and Payment Processing
                    </h3>
                    <p>
                      Tarana enables direct content upload and monetization for
                      creators, providing earnings dashboards and transparent payouts.
                      It manages a freelancing marketplace with secure job posting,
                      bidding, and an escrow system. Financial and KYC Data are
                      processed for secure transactions, payment releases, and
                      financial regulation compliance.
                    </p>
      
                    <h3 className="terms-h3">
                      Advertising &amp; Revenue Sharing
                    </h3>
                    <p>
                      The platform displays ads to free users and allows targeted ad
                      campaigns. Usage Data ensures smart ad placement and accurate
                      revenue share calculation. DPDPA mandates explicit consent for
                      processing personal data for targeted advertising and prohibits
                      it for children. The Privacy Policy distinguishes between
                      contextual and targeted advertising, obtaining explicit,
                      granular consent for the latter and providing opt-out options.
                    </p>
      
                    <h3 className="terms-h3">
                      Platform Improvement, Analytics, and Security
                    </h3>
                    <p>
                      Tarana uses aggregated and anonymized usage data to improve
                      platform performance, develop new features, and enhance overall
                      user experience. This data also supports security monitoring,
                      fraud detection, and the prevention of misuse or illegal
                      activity.
                    </p>
                    <p >
                      We do not use this data to identify individual users. All
                      analytics and security-related processing are conducted in
                      accordance with applicable data protection principles, including
                      purpose limitation and data minimization.
                    </p>
      
                    <h3 className="terms-h3">
                      UGC Controls, Moderation &amp; Banning
                    </h3>
                    <p>
                      User-Generated Content (UGC) is processed for compliance with
                      Tarana&apos;s <Link to="/content-policy">Content Poilcies</Link>,{" "}
                      <Link to="/community-guidelines">Community Guidelines</Link>, and
                      copyright laws. The platform reserves the right to moderate,
                      remove, or restrict violating content, and to suspend or ban
                      accounts for severe/repeated violations. As an
                      &quot;intermediary,&quot; Tarana has due diligence obligations
                      under Indian IT Rules 2021, necessitating active data processing
                      for content moderation. This processing is based on
                      &quot;legitimate use&quot; or &quot;legal obligation&quot; under
                      DPDPA and may involve automated flagging and human review.
                    </p>
                  </section>
      
                  <section className="terms-section" id="data-sharing">
                    <h2 className="terms-h2">
                      6. Data Sharing and Disclosure
                    </h2>
      
                    <p>
                      Tarana shares user data adhering to purpose limitation and
                      transparency.
                    </p>
      
                    <h3 className="terms-h3">
                      With Other Users
                    </h3>
                    <ul className="terms-list">
                      <li>
                        <strong>Public Profile Information:</strong> Profile name,
                        photo, public playlists, followers, and public content may be
                        visible to other users.
                      </li>
                      <li>
                        <strong>Collaborative Projects:</strong> Content within a
                        &quot;Band&quot; is shared with all current and future
                        members, who are granted licenses to use, modify, and
                        commercialize it within the Band&apos;s scope.
                      </li>
                      <li>
                        <strong>Freelancing Interactions:</strong> Freelance profiles,
                        portfolios, pricing, reviews, and project communications are
                        shared between clients and service providers.
                      </li>
                      <li>
                        <strong>Comments/Interactions:</strong> User-generated
                        comments, likes, and shares are visible to other users.
                      </li>
      
                      <p >
                        The Privacy Policy distinguishes between data that is public
                        by default (e.g., artist profiles) and data sharing that is
                        user-controlled (e.g., privacy settings for listening
                        activity), empowering users to manage their digital footprint.
                      </p>
                    </ul>
      
                    <h3 className="terms-h3">
                      With Third-Party Service Providers
                    </h3>
                    <p>
                      Tarana shares data with trusted third-party vendors for
                      essential services:
                    </p>
                    <ul className="terms-list">
                      <li>
                        <strong>Cloud Hosting &amp; Data Storage:</strong> For secure
                        content and user data storage.
                      </li>
                      <li>
                        <strong>Payment Gateways:</strong> For secure financial
                        transactions and payouts.
                      </li>
                      <li>
                        <strong>Analytics Providers:</strong> For platform usage
                        insights and service improvements.
                      </li>
                      <li>
                        <strong>Customer Support &amp; Communication Tools:</strong>{" "}
                        For user inquiries and notifications.
                      </li>
                      <li>
                        <strong>Security Services:</strong> For fraud detection and
                        platform security.
                      </li>
      
                      <p >
                        All third-party providers are contractually bound to protect
                        user data, adhere to DPDPA standards, and process data solely
                        for specified purposes.
                      </p>
                    </ul>
      
                    <h3 className="terms-h3">
                      For Legal Compliance and Law Enforcement
                    </h3>
                    <p>
                      Personal data may be disclosed when legally mandated (e.g.,
                      court order, government request). Tarana cooperates with law
                      enforcement to prevent, detect, investigate, or prosecute
                      offenses. Data may also be disclosed to protect Tarana&apos;s,
                      its users&apos;, or the public&apos;s rights, property, or
                      safety.
                    </p>
      
                    <h3 className="terms-h3">
                      In Event of Business Transfers
                    </h3>
                    <p>
                      In a merger, acquisition, or asset sale, personal data may be
                      transferred to a successor entity. Users will be notified of
                      such transfers.
                    </p>
                  </section>
      
                  <section className="terms-section" id="user-rights">
                    <h2 className="terms-h2">
                      7. User Rights and Control (Data Principal Rights)
                    </h2>
      
                    <p>
                      Tarana empowers users with comprehensive control over their
                      personal data, aligning with DPDPA provisions.
                    </p>
      
                    <ul className="terms-list">
                      <li>
                        <strong>Right to Access Information:</strong> Users can
                        request a summary of their personal data and processing
                        activities, including identities of other Data
                        Fiduciaries/Processors and data shared.
                      </li>
                      <li>
                        <strong>
                          Right to Correction, Completion, and Updating:
                        </strong>{" "}
                        Users can request correction of inaccurate/misleading data, or
                        completion/updating of incomplete data. Tarana will promptly
                        update data upon legitimate request.
                      </li>
                      <li>
                        <strong>Right to Erasure (Right to be Forgotten):</strong>{" "}
                        Users can request erasure of personal data when no longer
                        necessary or upon consent withdrawal. This right has
                        exceptions (e.g., legal compliance).
                      </li>
                      <li>
                        <strong>Right to Grievance Redressal:</strong> If you believe
                        your data has been mishandled, you have the right to file a
                        complaint with our Grievance Officer.
                      </li>
                      <li>
                        <strong>Right to Nominate:</strong> Users can nominate an
                        individual to exercise their data protection rights in case of
                        death or incapacitation.
                      </li>
                      <li>
                        <strong>Mechanism for Exercising Rights:</strong>
                        <ul className="terms-list">
                          <li>
                            <strong>In-App Settings:</strong> Controls for profile
                            visibility, content sharing, explicit content, and
                            advertising preferences.
                          </li>
                          <li>
                            <strong>Direct Contact:</strong> Users can contact
                            Tarana&apos;s Grievance Officer at{" "}
                            <a
                              href="mailto:help@taranamusic.in"
                              className="terms-link"
                            >
                              help@taranamusic.in
                            </a>{" "}
                            for access, correction, or erasure requests.
                          </li>
                          <li>
                            <strong>Consent Management:</strong> Consent can be
                            granted or withdrawn at any time through the profile
                            settings.
                          </li>
                        </ul>
                      </li>
                    </ul>
      
                    <h3
                      
                      className="terms-h4"
                    >
                      Table 2: Your Privacy Rights at Tarana
                    </h3>
                    <div className="terms-table-wrap">
                      <table className="terms-table">
                        <thead >
                          <tr>
                            <th className="terms-table-cell">
                              Your Right
                            </th>
                            <th className="terms-table-cell">
                              What it Means
                            </th>
                            <th className="terms-table-cell">
                              How to Exercise it on Tarana
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="terms-table-cell">
                              Right to Access
                            </td>
                            <td className="terms-table-cell">
                              Request a summary of your personal data and processing
                              activities.
                            </td>
                            <td className="terms-table-cell">
                              Via your account settings or by contacting{" "}
                              <a
                                href="mailto:help@taranamusic.in"
                                className="terms-link"
                              >
                                help@taranamusic.in
                              </a>
                              .
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Right to Correction/Update
                            </td>
                            <td className="terms-table-cell">
                              Request correction of inaccurate, incomplete, or
                              misleading data.
                            </td>
                            <td className="terms-table-cell">
                              Edit profile in account settings or contact{" "}
                              <a
                                href="mailto:help@taranamusic.in"
                                className="terms-link"
                              >
                                help@taranamusic.in
                              </a>
                              .
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Right to Erasure
                            </td>
                            <td className="terms-table-cell">
                              Request deletion of your data when no longer needed or
                              consent withdrawn.
                            </td>
                            <td className="terms-table-cell">
                              In-app content removal (playlists, tracks) or contact{" "}
                              <a
                                href="mailto:help@taranamusic.in"
                                className="terms-link"
                              >
                                help@taranamusic.in
                              </a>{" "}
                              for other data.
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Right to Grievance Redressal
                            </td>
                            <td className="terms-table-cell">
                              Lodge a complaint if you have concerns about data
                              processing.
                            </td>
                            <td className="terms-table-cell">
                              Contact our Grievance Officer via{" "}
                              <a
                                href="mailto:help@taranamusic.in"
                                className="terms-link"
                              >
                                help@taranamusic.in
                              </a>
                              .
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Right to Nominate
                            </td>
                            <td className="terms-table-cell">
                              Appoint someone to exercise your rights in case of
                              death/incapacity.
                            </td>
                            <td className="terms-table-cell">
                              Contact{" "}
                              <a
                                href="mailto:help@taranamusic.in"
                                className="terms-link"
                              >
                                help@taranamusic.in
                              </a>{" "}
                              for details on the nomination process.
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Right to Manage Consent
                            </td>
                            <td className="terms-table-cell">
                              Give, review, or withdraw consent for data processing.
                            </td>
                            <td className="terms-table-cell">
                              Through explicit in-app consent toggles and privacy
                              settings.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>
      
                  {/* Section 8 */}
                  <section className="terms-section" id="childrens-data-privacy">
                    <h2 className="terms-h2">
                      8. Children’s Data Privacy
                    </h2>
      
                    <p>
                      Tarana is committed to protecting the privacy of young users and
                      complying with the Digital Personal Data Protection Act, 2023
                      (DPDPA).
                    </p>
      
                    <h3 className="terms-h3">
                      Age Requirements
                    </h3>
                    <ul className="terms-list">
                      <li>
                        Users under the age of 13 are permitted to use Tarana’s core
                        features, including music streaming, content sharing,
                        community interaction, and collaboration tools.
                      </li>
                      <li>
                        However, users must be at least 18 years old to access any
                        financial features such as:
                        <ul className="terms-list">
                          <li>Freelancing</li>
                          <li>Monetization</li>
                          <li>Receiving payouts or earnings</li>
                        </ul>
                      </li>
                    </ul>
                    <p >
                      We do not collect payment or KYC data from users under 18.
                    </p>
                  </section>
      
                  {/* Section 9 */}
                  <section className="terms-section" id="data-security-retention">
                    <h2 className="terms-h2">
                      9. Data Security and Retention
                    </h2>
      
                    <p>
                      Tarana implements robust security measures and strict data
                      retention policies, ensuring DPDPA compliance.
                    </p>
      
                    <h3 className="terms-h3">
                      Security Safeguards
                    </h3>
                    <p>Tarana employs a multi-layered security approach:</p>
      
                    <h3 className="terms-h3">Technical Measures:</h3>
                    <ul className="terms-list">
                      <li>
                        End-to-End Encryption: Data encrypted in transit and at rest.
                      </li>
                      <li>
                        Multi-Factor Authentication (MFA): Enhanced user account
                        security.
                      </li>
                      <li>
                        Secure Payment Gateways and PCI DSS Compliance: For sensitive
                        payment information.
                      </li>
                      <li>
                        Regular Assessments: Periodic penetration testing and
                        vulnerability assessments.
                      </li>
                      <li>
                        Access Controls: Role-based access control (RBAC) and MFA for
                        internal data access.
                      </li>
                    </ul>
      
                    <h3 className="terms-h3">
                      Organizational Measures:
                    </h3>
                    <ul className="terms-list">
                      <li>
                        Incident Response Plan: Comprehensive Security Incident
                        Response Plan (SIRP).
                      </li>
                      <li>
                        Employee Training: Regular, mandatory internal training on
                        data protection.
                      </li>
                      <li>
                        Vendor Management: Vendor risk assessments and Data Processing
                        Agreements (DPAs) ensure third-party compliance.
                      </li>
                    </ul>
      
                    <p >
                      We continuously review and update our security protocols to
                      align with evolving best practices.
                    </p>
      
                    <h3 className="terms-h3">
                      Data Breach Notification
                    </h3>
                    <ul className="terms-list">
                      <li>
                        Notify affected users (Data Principals) without undue delay.
                      </li>
                      <li>
                        Inform the Data Protection Board of India, where required
                        under applicable law.
                      </li>
                    </ul>
      
                    <h3 className="terms-h3">
                      Data Retention Policy
                    </h3>
                    <p>
                      We retain personal data only for as long as necessary to fulfill
                      the purposes for which it was collected, or as required by law.
                    </p>
      
                    <h3 className="terms-h3">Key Retention Rules:</h3>
                    <ul className="terms-list">
                      <li>The original purpose has been fulfilled.</li>
                      <li>
                        The user withdraws consent (unless another legal basis
                        applies).
                      </li>
                      <li>
                        Retention is no longer required under any applicable law.
                      </li>
                    </ul>
                    <p >
                      Tarana performs periodic data audits to review and safely delete
                      redundant or outdated data.
                    </p>
      
                    <h3 className="terms-h3">
                      Special Case: Band Content
                    </h3>
                    <ul className="terms-list">
                      <li>
                        If a user leaves a Band, their contributed content generally
                        remains accessible to remaining members.
                      </li>
                      <li>
                        Individual users typically cannot unilaterally delete Band
                        content once it becomes part of a shared workspace.
                      </li>
                    </ul>
                  </section>
      
                  {/* Section 10 */}
                  <section className="terms-section" id="international-data-transfers">
                    <h2 className="terms-h2">
                      10. International Data Transfers
                    </h2>
                    <p>
                      Tarana may transfer personal data outside India, provided such
                      transfers are not to restricted countries and comply with all
                      DPDPA requirements, ensuring adequate protection consistent with
                      Indian law.
                    </p>
                  </section>
      
                  {/* Section 11 */}
                  <section className="terms-section" id="grievance-redressal">
                    <h2 className="terms-h2">
                      11. Grievance Redressal Mechanism
                    </h2>
      
                    <h3 className="terms-h3">
                      Internal Grievance System
                    </h3>
                    <p>
                      Users should first contact Tarana&apos;s designated Grievance
                      Officer at
                      <a
                        href="mailto:help@taranamusic.in"
                        className="terms-link"
                      >
                        {" "}
                        help@taranamusic.in{" "}
                      </a>
                      for data-related queries or complaints. Tarana is committed to
                      timely grievance resolution. An efficient internal system
                      resolves issues promptly, preventing escalation to the Data
                      Protection Board.
                    </p>
                  </section>
      
                  {/* Section 12 */}
                  <section className="terms-section" id="policy-updates">
                    <h2 className="terms-h2">
                      12. Updates to the Privacy Policy
                    </h2>
                    <p>
                      Tarana reserves the right to modify this Privacy Policy at any
                      time. Users will be notified of material changes by posting the
                      updated policy and last updated date. Continued use after
                      modifications constitutes acceptance. If a user disagrees, they
                      must cease using the Services immediately.
                    </p>
                  </section>
      
                  {/* Section 13 */}
                  <section className="terms-section" id="contact-information">
                    <h2 className="terms-h2">
                      13. Contact Information
                    </h2>
                    <p>
                      For questions or concerns regarding this Privacy Policy or
                      Tarana&apos;s data handling, contact Tarana at:
                      <a
                        href="mailto:help@taranamusic.in"
                        className="terms-link"
                      >
                        {" "}
                        help@taranamusic.in
                      </a>
                      .
                    </p>
                  </section>
      
                  <p className="text-sm text-gray-500">
                    <strong>Last Updated:</strong> August 11, 2025
                  </p>
    </LegalPageLayout>
  );
}
